import { useCallback, useEffect, useState, type CSSProperties, type JSX } from 'react'
import { api, ApiError, type AssignableUser, type SavedList, type User } from '../api.js'
import { T } from '../tokens.js'

/**
 * Saved lists: choosing one, making one, filling one, and handing one to a rep.
 *
 * ## This surface is invented, not transcribed
 *
 * The mock's `Saved lists`, `New list` and `Export` buttons have no handlers,
 * and there is no owner field, assign control or team view anywhere in it. So
 * this is designed to the mock's visual language — square panels, 1.5px rules,
 * 10px uppercase micro-labels, the navy primary button, `tokens.ts` throughout
 * — and deliberately kept to the five things R-16 AC-4 needs. It is not a
 * list-management application.
 *
 * **`Export` is not here.** No handler in the mock, no format named anywhere in
 * the design record, and no endpoint in spec §6. A button that produces an
 * unspecified file is worse than no button, and this codebase has removed three
 * inert controls already for the same reason (the sign-in screen's "keep me
 * signed in", the three unbuilt tabs' silence, `M. Reyes · West Valley`).
 *
 * ## Selecting a list is a server filter, not a browser one
 *
 * The rest of the targeting list fetches the book once and filters in the
 * browser, because `confidence` and `enrollmentStatus` are single-valued on the
 * wire and this screen's rail toggles three of each (see `FilterRail.tsx`).
 * `?savedListId=` is different: it is a real filter the route supports, it
 * resolves the membership against the run's own estimates, and it is the only
 * way to see a list whose accounts the default view suppresses. So selecting a
 * list re-fetches, and `TargetingList` owns that.
 *
 * ## 403 and 404 are different answers and are rendered differently
 *
 * This is a ruling from Plan 3's review, not an implementation detail.
 *
 * - `GET /api/accounts?savedListId=` and `GET /api/lists/:id` answer **404**
 *   for a list that does not exist *and* for one the caller may not read,
 *   byte-identically, so that the routes are not an existence oracle for list
 *   ids. A caller therefore learns nothing from a 404 except "not available to
 *   you, now" — and that is what `LIST_GONE` says. It must not read as a
 *   permission refusal, because the caller cannot tell which it was.
 * - `PATCH /api/lists/:id/assignment` answers **403** to a caller who *can*
 *   read the list and does not own it. That caller already knows the list
 *   exists — they are looking at it — so there is no oracle left to protect and
 *   "no such list" would be a falsehood that costs somebody an afternoon.
 *   `NOT_OWNER` names the actual problem and what to do about it.
 *
 * Both strings are pinned as literals in `ListsPanel.test.tsx`, not imported
 * into the assertion, so rewriting one turns a test red.
 */

/** A 404 from any list-scoped route. Deliberately says nothing about permission — see the docblock. */
export const LIST_GONE = 'That list is no longer available. It may have been deleted.'

/** A 403 from the assignment route, and only from there. */
export const NOT_OWNER
  = 'You are not the owner of this list, so you cannot reassign it. Ask its owner to hand it on.'

/** The whole book: no `savedListId`, the program's default view, decline suppression and all. */
export const MY_BOOK_LABEL = 'My book'

const NEW_LIST_LABEL = 'New list'
const SAVE_LABEL = 'Save list'
const NAME_LABEL = 'List name'
const ASSIGN_LABEL = 'Assign'
const UNASSIGNED_OPTION = 'Nobody — unassigned'

const LISTS_FAILED = 'Your saved lists could not be loaded.'

const panel: CSSProperties = {
  background: '#fff',
  borderBottom: `1.5px solid ${T.color.rule}`,
  padding: '16px 18px',
}

const sectionLabel: CSSProperties = {
  fontSize: T.micro.size,
  fontWeight: T.weight.semi,
  letterSpacing: T.micro.tracking,
  textTransform: T.micro.transform,
  color: T.color.micro,
}

const helperText: CSSProperties = {
  fontSize: 11.5,
  lineHeight: 1.45,
  color: T.color.muted,
  marginTop: 9,
}

const rowButton: CSSProperties = {
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  textAlign: 'left',
  padding: '9px 11px',
  marginTop: 6,
  fontFamily: T.fontFamily,
  fontSize: 12.5,
  fontWeight: T.weight.semi,
  borderRadius: T.radius.square,
  cursor: 'pointer',
}

const selectStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  marginTop: 9,
  padding: '10px 11px',
  border: `1.5px solid ${T.color.rule}`,
  borderRadius: T.radius.square,
  background: '#fff',
  color: T.color.ink,
  fontFamily: T.fontFamily,
  fontSize: 12.5,
}

const textInput: CSSProperties = { ...selectStyle }

const primaryButton: CSSProperties = {
  marginTop: 9,
  border: `1.5px solid ${T.color.navy}`,
  background: T.color.navy,
  color: '#fff',
  padding: '9px 13px',
  fontFamily: T.fontFamily,
  fontSize: T.micro.size,
  fontWeight: T.weight.bold,
  letterSpacing: '0.08em',
  textTransform: T.micro.transform,
  borderRadius: T.radius.button,
  cursor: 'pointer',
}

const secondaryButton: CSSProperties = {
  ...primaryButton,
  border: `1.5px solid ${T.color.border}`,
  background: '#fff',
  color: T.color.ink,
}

/**
 * A disabled primary button has to *look* disabled.
 *
 * Seen in the browser: `ADD 0 ACCOUNTS` drawn in full navy with a pointer
 * cursor while `disabled` was set. It is the mock's own defect in reverse —
 * there, controls looked live and did nothing; here one looked live and was
 * refused. Both leave a rep pressing something that will not move.
 */
function primary(disabled: boolean): CSSProperties {
  return disabled
    ? { ...primaryButton, border: `1.5px solid ${T.color.chipOff}`, background: T.color.chipOff,
        color: '#fff', cursor: 'not-allowed' }
    : primaryButton
}

function selectedStyle(active: boolean): CSSProperties {
  return active
    ? { ...rowButton, border: `1.5px solid ${T.color.navy}`, background: T.tint.blue, color: T.color.navy }
    : { ...rowButton, border: `1.5px solid ${T.color.rule}`, background: '#fff', color: T.color.ink }
}

/** `12 accounts`, `1 account`. Counted off the membership the list route already sent. */
function countLabel(list: SavedList): string {
  return list.accountIds.length === 1 ? '1 account' : `${list.accountIds.length} accounts`
}

type Notice = { tone: 'ok' | 'error'; text: string }

export interface ListsPanelProps {
  /** The signed-in user. Needed for the id, not only the role — "assigned to you" is a fact about the id. */
  user: User
  /** The list currently applied to the table, or `null` for the whole book. */
  selected: SavedList | null
  /**
   * The list to apply, or `null` for the book.
   *
   * The whole list rather than its id: the screen above needs the name for its
   * subtitle and the membership count for its empty state, and it already
   * arrived on this panel's own `GET /api/lists`. Passing the id alone would
   * make the screen fetch what this component is holding.
   */
  onSelect: (list: SavedList | null) => void
  /**
   * The accounts the table is showing right now — what `Add` adds.
   *
   * Deliberately "shown", not "the whole book": the rep has filtered the list
   * down to the accounts they mean, and a button that quietly added the other
   * two hundred would be the last time they pressed it.
   */
  shownAccountIds: readonly string[]
}

export function ListsPanel({ user, selected, onSelect, shownAccountIds }: ListsPanelProps): JSX.Element {
  const [lists, setLists] = useState<SavedList[] | null>(null)
  const [loadFailed, setLoadFailed] = useState<string | null>(null)
  const [naming, setNaming] = useState(false)
  const [name, setName] = useState('')
  const [target, setTarget] = useState('')
  const [assignee, setAssignee] = useState('')
  const [assignable, setAssignable] = useState<readonly AssignableUser[]>([])
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<Notice | null>(null)

  /**
   * Selecting clears the last confirmation. `Added 43 accounts to “Tuesday
   * calls”.` is a true sentence about something that has just happened, and
   * leaving it up while the rep moves to a different list turns it into a
   * sentence about the list they are now looking at. Seen in a browser, three
   * screens after the fact.
   */
  const select = (list: SavedList | null): void => {
    setNotice(null)
    onSelect(list)
  }

  const reload = useCallback(async (): Promise<SavedList[]> => {
    const fetched = await api.lists()
    setLists(fetched)
    return fetched
  }, [])

  useEffect(() => {
    let cancelled = false
    void api.lists()
      .then((fetched) => { if (!cancelled) setLists(fetched) })
      .catch((error: unknown) => {
        if (cancelled) return
        setLoadFailed(error instanceof ApiError ? error.message : LISTS_FAILED)
      })
    return () => { cancelled = true }
  }, [])

  // The roster is fetched only for the one role the route serves. Firing a
  // request every rep's session answers 403 to would put a red line in every
  // rep's network log to learn a fact the session already carries.
  const canAssign = user.role === 'program_manager'
  useEffect(() => {
    if (!canAssign) return undefined
    let cancelled = false
    void api.assignableUsers()
      .then((users) => { if (!cancelled) setAssignable(users) })
      // A roster that will not load costs the assign control and nothing else.
      // Every other control on this panel still works, so this is not a place
      // to fail the panel.
      .catch(() => undefined)
    return () => { cancelled = true }
  }, [canAssign])

  const nameOf = (userId: string): string | null =>
    assignable.find((candidate) => candidate.id === userId)?.displayName ?? null

  /**
   * The one line under a list's name, or nothing.
   *
   * The fourth case — someone else's list with no assignee — cannot occur:
   * `canReadSavedList` admits a list the caller does not own only through the
   * assignee clause or the program-manager clause, and both require a non-null
   * `assigned_to_id`. So there is no branch for it rather than a branch that
   * says something untrue about a state that never arrives.
   */
  const ownership = (list: SavedList): string | null => {
    if (list.assignedToId === user.id) return 'Assigned to you'
    if (list.assignedToId !== null) return `Assigned to ${nameOf(list.assignedToId) ?? 'a rep'}`
    return null
  }

  const failureText = (error: unknown, forbidden: string): string => {
    if (!(error instanceof ApiError)) return 'Something went wrong. Try again.'
    if (error.status === 404) return LIST_GONE
    if (error.status === 403) return forbidden
    return error.message
  }

  const createNew = (): void => {
    const trimmed = name.trim()
    if (trimmed === '' || busy) return
    setBusy(true)
    setNotice(null)
    void api.createList(trimmed)
      .then(async () => {
        await reload()
        setNaming(false)
        setName('')
        setNotice({ tone: 'ok', text: `Created “${trimmed}”. It has no accounts in it yet.` })
      })
      .catch((error: unknown) => { setNotice({ tone: 'error', text: failureText(error, NOT_OWNER) }) })
      .finally(() => { setBusy(false) })
  }

  const addShown = (): void => {
    if (target === '' || shownAccountIds.length === 0 || busy) return
    setBusy(true)
    setNotice(null)
    const ids = [...shownAccountIds]
    // One request per account: `POST /api/lists/:id/accounts` takes a single
    // `accountId` and there is no bulk-add route (`POST /api/lists` is the only
    // endpoint that accepts an `accountIds` array, and only at creation). Run
    // in sequence rather than in parallel so a failure stops at the first one
    // instead of firing the remaining two hundred anyway. Reported as a
    // finding; not worked around by inventing a route.
    void (async () => {
      for (const accountId of ids) await api.addToList(target, accountId)
    })()
      .then(async () => {
        const fetched = await reload()
        const into = fetched.find((list) => list.id === target)
        setNotice({
          tone: 'ok',
          text: `Added ${ids.length === 1 ? '1 account' : `${ids.length} accounts`} to “${into?.name ?? 'the list'}”.`,
        })
      })
      .catch((error: unknown) => { setNotice({ tone: 'error', text: failureText(error, NOT_OWNER) }) })
      .finally(() => { setBusy(false) })
  }

  const assign = (): void => {
    if (selected === null || busy) return
    setBusy(true)
    setNotice(null)
    const to = assignee === '' ? null : assignee
    void api.assignList(selected.id, to)
      .then(async (updated) => {
        await reload()
        onSelect(updated)
        setNotice({
          tone: 'ok',
          text: to === null
            ? `“${updated.name}” is no longer assigned to anyone.`
            : `Assigned “${updated.name}” to ${nameOf(to) ?? 'that rep'}.`,
        })
      })
      .catch((error: unknown) => { setNotice({ tone: 'error', text: failureText(error, NOT_OWNER) }) })
      .finally(() => { setBusy(false) })
  }

  const known = lists ?? []
  // Assignment is owner-only on the server, and this is the client half of that
  // — not a second copy of the rule. The server still refuses, and the 403 path
  // above still renders `NOT_OWNER`; drawing a control that is guaranteed to be
  // refused is the defect Task 11 removed from the unbuilt tabs.
  const mayAssignSelected = canAssign && selected !== null && selected.ownerId === user.id

  return (
    <div>
      <div style={panel}>
        <div id="saved-lists-label" style={sectionLabel}>Saved lists</div>
        <div role="group" aria-labelledby="saved-lists-label">
          <button
            type="button"
            aria-pressed={selected === null}
            onClick={() => { select(null) }}
            style={selectedStyle(selected === null)}
          >
            {MY_BOOK_LABEL}
          </button>
          {known.map((list) => {
            const note = ownership(list)
            return (
              <button
                key={list.id}
                type="button"
                aria-pressed={selected !== null && selected.id === list.id}
                onClick={() => { select(list) }}
                style={selectedStyle(selected !== null && selected.id === list.id)}
              >
                {list.name}
                <span style={{ display: 'block', fontSize: 11, fontWeight: T.weight.body, color: T.color.micro }}>
                  {note === null ? countLabel(list) : `${countLabel(list)} · ${note}`}
                </span>
              </button>
            )
          })}
        </div>

        {loadFailed === null ? null : <div style={{ ...helperText, color: T.color.red }}>{loadFailed}</div>}

        {naming
          ? (
            <div style={{ marginTop: 12 }}>
              <label htmlFor="new-list-name" style={sectionLabel}>{NAME_LABEL}</label>
              <input
                id="new-list-name"
                type="text"
                value={name}
                onChange={(event) => { setName(event.target.value) }}
                style={textInput}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={createNew}
                  disabled={busy || name.trim() === ''}
                  style={primary(busy || name.trim() === '')}
                >
                  {SAVE_LABEL}
                </button>
                <button
                  type="button"
                  onClick={() => { setNaming(false); setName('') }}
                  style={secondaryButton}
                >
                  Cancel
                </button>
              </div>
            </div>
          )
          : (
            <button type="button" onClick={() => { setNaming(true) }} style={secondaryButton}>
              {NEW_LIST_LABEL}
            </button>
          )}
      </div>

      {known.length === 0
        ? null
        : (
          <div style={panel}>
            <label htmlFor="add-to-list" style={sectionLabel}>Add the accounts shown to</label>
            <select
              id="add-to-list"
              value={target}
              onChange={(event) => { setTarget(event.target.value) }}
              style={selectStyle}
            >
              <option value="">Choose a list…</option>
              {known.map((list) => <option key={list.id} value={list.id}>{list.name}</option>)}
            </select>
            <button
              type="button"
              onClick={addShown}
              disabled={busy || target === '' || shownAccountIds.length === 0}
              style={primary(busy || target === '' || shownAccountIds.length === 0)}
            >
              {shownAccountIds.length === 1 ? 'Add 1 account' : `Add ${shownAccountIds.length} accounts`}
            </button>
          </div>
        )}

      {mayAssignSelected
        ? (
          <div style={panel}>
            <label htmlFor="assign-to" style={sectionLabel}>Assign this list to</label>
            <select
              id="assign-to"
              value={assignee}
              onChange={(event) => { setAssignee(event.target.value) }}
              style={selectStyle}
            >
              <option value="">{UNASSIGNED_OPTION}</option>
              {assignable.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>{candidate.displayName}</option>
              ))}
            </select>
            <button type="button" onClick={assign} disabled={busy} style={primary(busy)}>
              {ASSIGN_LABEL}
            </button>
          </div>
        )
        : null}

      {notice === null
        ? null
        : (
          <div
            role="status"
            style={{
              ...panel,
              fontSize: 12,
              lineHeight: 1.45,
              color: notice.tone === 'error' ? T.color.declinedInk : T.color.body,
              background: notice.tone === 'error' ? T.tint.red : T.tint.green,
            }}
          >
            {notice.text}
          </div>
        )}
    </div>
  )
}
